import React from 'react';

/**
 * BucksDisplay — Reusable currency display component.
 * Shows a deer icon + formatted Bucks amount.
 *
 * Props:
 *   amount  — numeric balance (required)
 *   size    — 'sm' | 'md' | 'lg' (default 'md')
 *   color   — text color override (default based on amount sign)
 *   showIcon — show the deer icon (default true)
 *   label   — optional label like "Balance:" before the amount
 *   style   — additional container styles
 */
export default function BucksDisplay({ amount = 0, size = 'md', color, showIcon = true, label, style = {} }) {
  const sizes = {
    sm: { fontSize: 12, iconSize: 14, gap: 4 },
    md: { fontSize: 16, iconSize: 18, gap: 5 },
    lg: { fontSize: 24, iconSize: 26, gap: 6 },
    xl: { fontSize: 32, iconSize: 34, gap: 8 },
  };

  const s = sizes[size] || sizes.md;
  const displayColor = color || (amount >= 0 ? '#fbbf24' : '#f85149');
  const formatted = typeof amount === 'number' ? amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : amount;

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: s.gap,
      fontSize: s.fontSize,
      fontWeight: 700,
      color: displayColor,
      fontFamily: "'Georgia', serif",
      ...style
    }}>
      {label && <span style={{ fontWeight: 400, color: '#8b949e', marginRight: 2 }}>{label}</span>}
      {showIcon && (
        <img
          src="/bucks-icon.svg"
          alt=""
          style={{ width: s.iconSize, height: s.iconSize, verticalAlign: 'middle' }}
        />
      )}
      <span>{formatted}</span>
    </span>
  );
}

/**
 * Inline Bucks badge — compact pill for headers and tables.
 */
export function BucksBadge({ amount = 0, style = {} }) {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      background: '#1a2332',
      padding: '4px 10px',
      borderRadius: 12,
      fontSize: 12,
      fontWeight: 600,
      color: '#fbbf24',
      border: '1px solid #fbbf2440',
      ...style
    }}>
      <img src="/bucks-icon.svg" alt="" style={{ width: 14, height: 14 }} />
      {typeof amount === 'number' ? amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : amount}
    </span>
  );
}
